/*
 * Project Page Panel Template
 *
 * Includes:
 * - Project Page
 * - Asset Viewer Page
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  ProjectPanelArea,
} from '../types';
import {
  AI_DEFAULT_MIN_WIDTH,
  AI_DEFAULT_MIN_WIDTH_COMPACT,
  AI_DEFAULT_WIDTH,
  DETAILS_DEFAULT_WIDTH,
  DETAILS_MIN_WIDTH,
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
  PROJECT_NAV_MIN_WIDTH,
  PROJECT_NAV_MIN_WIDTH_COMPACT,
} from './constants';
import TEXT from './generated/TEXT/project';

TEXT.DEFINE({
  /** Project navigation panel toggle labels */
  directoryNav: {
    /** Aria label for toggling project navigation */
    accessibility: 'Toggle the Project Navigation',
    /** Label when hiding project navigation */
    hide: 'Hide Project Navigation',
    /** Label when showing project navigation */
    show: 'Show Project Navigation',
  },
  /** Asset viewer panel toggle labels */
  viewer: {
    /** Aria label for toggling asset viewer */
    accessibility: 'Toggle the Asset Viewer',
    /** Label when hiding asset viewer */
    hide: 'Hide Asset Viewer',
    /** Label when showing asset viewer */
    show: 'Show Asset Viewer',
  },
  /** Asset info panel toggle labels */
  details: {
    /** Aria label for toggling asset info */
    accessibility: 'Toggle the Asset Info',
    /** Label when hiding asset info */
    hide: 'Hide Asset Info',
    /** Label when showing asset info */
    show: 'Show Asset Info',
  },
  /** AI panel toggle labels */
  ai: {
    /** Aria label for toggling AI panel */
    accessibility: 'Toggle the AI Panel',
    /** Label when hiding AI panel */
    hide: 'Hide AI Panel',
    /** Label when showing AI panel */
    show: 'Show AI Panel',
  },
});

/**
 * Base template (Mobile +)
 */
export function baseTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryContent: 'right',
      viewer: 'right',
      details: 'right',
    },
    columns: [
      /* Project Nav uses the PrimarySheetNav tunnel on smaller screens so it's always hidden. */
      {
        key: 'directoryNav',
        defaultSize: MEDIUM_NAV_MIN_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Projects & Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 358,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* AI Panel */
      {
        key: 'ai',
        defaultMin: AI_DEFAULT_MIN_WIDTH,
        defaultSize: AI_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
      viewer: 'right',
      details: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: PROJECT_NAV_MIN_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          variant: 'left',
          label: TEXT.directoryNav,
        },
        showing: visible.includes('directoryNav'),
      },

      /* Projects & Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 358,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_MIN_WIDTH,
        defaultMax: 400,
        isToggleable: false,
        showing: false,
      },

      /* AI Panel */
      {
        key: 'ai',
        defaultMin: AI_DEFAULT_MIN_WIDTH,
        defaultSize: AI_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
      details: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH_COMPACT,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          variant: 'left',
          label: TEXT.directoryNav,
        },
        showing: visible.includes('directoryNav'),
      },

      /* Projects & Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: true,
        panelToggle: {
          variant: 'center',
          label: TEXT.viewer,
        },
        showing: visible.includes('viewer'),
      },

      /* Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        panelToggle: {
          variant: 'center',
          label: TEXT.details,
        },
        showing: visible.includes('details'),
      },

      /* AI Panel */
      {
        key: 'ai',
        defaultMin: AI_DEFAULT_MIN_WIDTH_COMPACT,
        defaultSize: AI_DEFAULT_WIDTH,
        isToggleable: true,
        panelToggle: {
          variant: 'right',
          label: TEXT.ai,
        },
        showing: visible.includes('ai'),
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<ProjectPanelArea> => {
  return ['directoryNav', 'directoryContent'];
};

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
